import { SsoProviderKey } from "../../utilities/helpers/pageVocabulary.generated";
import { LinkedInRedirect, readLinkedInRedirect } from "./linkedInRedirect";

export type SignupRequest = { provider: Extract<SsoProviderKey, "Microsoft" | "LinkedIn">; zoneIds: number[] };

const SIGNUP_PROVIDERS: Record<string, SignupRequest["provider"]> = { microsoft: "Microsoft", linkedin: "LinkedIn" };

const CHOSEN_ZONES_KEY = "eclipse_demo_chosen_zones";

const LINKEDIN_NONCE_KEY = "eclipse_linkedin_sign_in_nonce";

const MICROSOFT_SIGN_IN_KEY = "eclipse_microsoft_sign_in_begun";

/** The sign-up the landing page sent this visitor here for, as ?signup=linkedin&zones=3,5. */
export function readSignupRequest(search: string): SignupRequest | null {
  const query = new URLSearchParams(search);
  const provider = SIGNUP_PROVIDERS[query.get("signup")?.toLowerCase() ?? ""];
  if (!provider) return null;

  const zoneIds = (query.get("zones") ?? "")
    .split(",")
    .filter(part => /^\d+$/.test(part))
    .map(Number);

  return { provider, zoneIds };
}

export type SsoArrival =
  | { kind: "linkedIn"; redirect: LinkedInRedirect }
  | { kind: "signup"; request: SignupRequest }
  | { kind: "none" };

/** Why the browser is on the login page: back from LinkedIn, sent by the landing page to sign up, or neither. */
export function readSsoArrival(search: string): SsoArrival {
  const redirect = readLinkedInRedirect(search);
  if (redirect) return { kind: "linkedIn", redirect };

  const request = readSignupRequest(search);
  return request ? { kind: "signup", request } : { kind: "none" };
}

export function rememberChosenZones(zoneIds: number[]) {
  try {
    sessionStorage.setItem(CHOSEN_ZONES_KEY, JSON.stringify(zoneIds));
  } catch {
  }
}

export function beginLinkedInSignIn(): string {
  const nonce = crypto.randomUUID();
  try {
    sessionStorage.setItem(LINKEDIN_NONCE_KEY, nonce);
  } catch {
  }

  return nonce;
}

export function takeLinkedInNonce(): string | null {
  try {
    const nonce = sessionStorage.getItem(LINKEDIN_NONCE_KEY);
    sessionStorage.removeItem(LINKEDIN_NONCE_KEY);
    return nonce;
  } catch {
    return null;
  }
}

export function beginMicrosoftSignIn() {
  try {
    sessionStorage.setItem(MICROSOFT_SIGN_IN_KEY, "1");
  } catch {
  }
}

/** Whether this browser began a Microsoft sign-in still to be finished; true once per sign-in. */
export function takeMicrosoftSignIn(): boolean {
  try {
    const begun = sessionStorage.getItem(MICROSOFT_SIGN_IN_KEY) !== null;
    sessionStorage.removeItem(MICROSOFT_SIGN_IN_KEY);
    return begun;
  } catch {
    return false;
  }
}

export function takeChosenZones(): number[] {
  try {
    const stored = sessionStorage.getItem(CHOSEN_ZONES_KEY);
    sessionStorage.removeItem(CHOSEN_ZONES_KEY);

    const zoneIds: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(zoneIds) ? zoneIds.filter((id): id is number => Number.isInteger(id)) : [];
  } catch {
    return [];
  }
}
